import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import { LoadingSpinner, ErrorBanner } from '../components/AdminStatus';

const API  = 'https://api.udanchu.com';
const font = "'DM Sans', system-ui, sans-serif";

const getKey = () => sessionStorage.getItem('udanchu_admin_key') || '';

const STATUS_STYLES = {
  active:     { bg: 'rgba(52,211,153,0.12)',  border: 'rgba(52,211,153,0.3)',  color: '#34d399' },
  draft:      { bg: 'rgba(251,191,36,0.12)',  border: 'rgba(251,191,36,0.3)',  color: '#fbbf24' },
  deprecated: { bg: 'rgba(248,113,113,0.12)', border: 'rgba(248,113,113,0.3)', color: '#f87171' },
};
const statusStyle = s => STATUS_STYLES[s] || STATUS_STYLES.deprecated;

const DOMAIN_COLORS = {
  traffic:  { bg: 'rgba(79,142,247,0.15)',  color: '#4f8ef7' },
  consumer: { bg: 'rgba(52,211,153,0.15)',  color: '#34d399' },
  police:   { bg: 'rgba(251,191,36,0.15)',  color: '#fbbf24' },
};
const domainStyle = d => DOMAIN_COLORS[d] || { bg: 'rgba(100,116,139,0.15)', color: '#64748b' };

const s = {
  topBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '24px',
  },
  topLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  backBtn: {
    background: 'transparent',
    border: '1px solid #252a38',
    borderRadius: '7px',
    color: '#64748b',
    fontFamily: font,
    fontSize: '13px',
    fontWeight: '500',
    padding: '7px 14px',
    cursor: 'pointer',
  },
  cardId: {
    color: '#64748b',
    fontFamily: "'DM Mono', monospace",
    fontSize: '13px',
  },
  editBtn: {
    background: '#4f8ef7',
    border: 'none',
    borderRadius: '7px',
    color: '#fff',
    fontFamily: font,
    fontSize: '13px',
    fontWeight: '600',
    padding: '7px 18px',
    cursor: 'pointer',
  },
  card: {
    background: '#181c27',
    border: '1px solid #252a38',
    borderRadius: '10px',
    padding: '28px',
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  headerRow: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: '16px',
    flexWrap: 'wrap',
  },
  title: {
    color: '#e2e8f0',
    fontFamily: font,
    fontSize: '20px',
    fontWeight: '700',
    margin: 0,
    flex: 1,
  },
  badges: {
    display: 'flex',
    gap: '8px',
    flexShrink: 0,
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  badge: {
    borderRadius: '4px',
    fontSize: '11px',
    fontWeight: '700',
    letterSpacing: '0.06em',
    padding: '3px 8px',
    textTransform: 'uppercase',
  },
  statusBadge: {
    borderRadius: '4px',
    fontSize: '11px',
    fontWeight: '600',
    letterSpacing: '0.04em',
    padding: '3px 8px',
    border: '1px solid',
  },
  divider: {
    border: 'none',
    borderTop: '1px solid #252a38',
    margin: 0,
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  sectionLabel: {
    color: '#64748b',
    fontFamily: font,
    fontSize: '11px',
    fontWeight: '600',
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    margin: 0,
  },
  sectionText: {
    color: '#e2e8f0',
    fontFamily: font,
    fontSize: '14px',
    lineHeight: '1.6',
    margin: 0,
  },
  pill: {
    display: 'inline-block',
    background: '#0f1117',
    border: '1px solid #252a38',
    borderRadius: '6px',
    color: '#e2e8f0',
    fontFamily: font,
    fontSize: '13px',
    padding: '5px 12px',
    marginRight: '8px',
    marginBottom: '8px',
  },
  lawRefItem: {
    background: '#0f1117',
    border: '1px solid #252a38',
    borderRadius: '8px',
    padding: '12px 16px',
    marginBottom: '8px',
  },
  lawRefTitle: {
    color: '#e2e8f0',
    fontFamily: font,
    fontSize: '13px',
    fontWeight: '600',
    marginBottom: '4px',
  },
  lawRefMeta: {
    color: '#64748b',
    fontFamily: font,
    fontSize: '12px',
    marginBottom: '4px',
  },
  lawRefSummary: {
    color: '#94a3b8',
    fontFamily: font,
    fontSize: '13px',
    lineHeight: '1.5',
  },
  unverifiedTag: {
    display: 'inline-block',
    background: 'rgba(248,113,113,0.1)',
    border: '1px solid rgba(248,113,113,0.3)',
    borderRadius: '4px',
    color: '#f87171',
    fontFamily: font,
    fontSize: '10px',
    fontWeight: '600',
    padding: '1px 6px',
    marginLeft: '8px',
    verticalAlign: 'middle',
  },
};

function Section({ label, children }) {
  return (
    <div style={s.section}>
      <p style={s.sectionLabel}>{label}</p>
      {children}
    </div>
  );
}

function LawRefList({ refs }) {
  if (!refs?.length) return <p style={{ ...s.sectionText, color: '#64748b' }}>—</p>;
  return (
    <div>
      {refs.map((r, i) => (
        <div key={i} style={s.lawRefItem}>
          <div style={s.lawRefTitle}>
            {r.act}
            {r.section && <span style={{ color: '#64748b' }}> § {r.section}</span>}
            {r.verified === false && <span style={s.unverifiedTag}>unverified</span>}
          </div>
          {r.summary && <p style={s.lawRefSummary}>{r.summary}</p>}
        </div>
      ))}
    </div>
  );
}

function PillList({ items }) {
  if (!items?.length) return <p style={{ ...s.sectionText, color: '#64748b' }}>—</p>;
  return (
    <div>
      {items.map((item, i) => (
        <span key={i} style={s.pill}>{item}</span>
      ))}
    </div>
  );
}

function NumberedList({ items }) {
  if (!items?.length) return <p style={{ ...s.sectionText, color: '#64748b' }}>—</p>;
  return (
    <ol style={{ margin: 0, paddingLeft: '20px' }}>
      {items.map((item, i) => (
        <li key={i} style={{ ...s.sectionText, marginBottom: '6px' }}>{item}</li>
      ))}
    </ol>
  );
}

export default function AdminCardDetail() {
  const { id }       = useParams();
  const navigate     = useNavigate();
  const [card,    setCard]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    fetch(`${API}/cards/${id}`, { headers: { 'X-Admin-Key': getKey() } })
      .then(r => r.ok ? r.json() : r.json().then(j => Promise.reject(j.message || `Error ${r.status}`)))
      .then(data => { if (!cancelled) setCard(data); })
      .catch(err  => { if (!cancelled) setError(typeof err === 'string' ? err : 'Failed to load card.'); })
      .finally(()  => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [id, attempt]);

  const ds = card ? domainStyle(card.domain) : {};
  const ss = card ? statusStyle(card.status) : {};

  return (
    <AdminLayout title="Card Detail">
      <div style={s.topBar}>
        <div style={s.topLeft}>
          <button style={s.backBtn} onClick={() => navigate('/admin/cards')}>← Back</button>
          {card && <span style={s.cardId}>{card.id}</span>}
        </div>
        {card && (
          <button style={s.editBtn} onClick={() => navigate(`/admin/card/${id}/edit`)}>
            Edit Card
          </button>
        )}
      </div>

      {loading && <LoadingSpinner message="Loading card..." />}

      {!loading && error && (
        <ErrorBanner error={error} onRetry={() => setAttempt(a => a + 1)} />
      )}

      {!loading && !error && card && (
        <div style={s.card}>
          <div style={s.headerRow}>
            <h1 style={s.title}>{card.title}</h1>
            <div style={s.badges}>
              <span style={{ ...s.badge, background: ds.bg, color: ds.color }}>
                {card.domain || 'general'}
              </span>
              <span style={{ ...s.statusBadge, background: ss.bg, borderColor: ss.border, color: ss.color }}>
                {card.status}
              </span>
            </div>
          </div>

          <hr style={s.divider} />

          <Section label="Short Answer">
            <p style={s.sectionText}>{card.short_answer || '—'}</p>
          </Section>

          <Section label="Explanation">
            <p style={s.sectionText}>{card.explanation || '—'}</p>
          </Section>

          <hr style={s.divider} />

          <Section label="Law References">
            <LawRefList refs={card.law_references} />
          </Section>

          <hr style={s.divider} />

          <Section label="Trigger Queries">
            <PillList items={card.trigger_queries} />
          </Section>

          <Section label="Action Steps">
            <NumberedList items={card.action_steps} />
          </Section>

          <Section label="Edge Cases">
            <PillList items={card.edge_cases} />
          </Section>
        </div>
      )}
    </AdminLayout>
  );
}
